import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingDown, TrendingUp } from 'lucide-react';
import { Button, Accordion, Card } from '@/components/ui';
import { useBunkerPricesStore } from '@/store/bunkerPricesStore';
import { BunkerPricesPreviewSkeleton } from './BunkerPricesPreviewSkeleton';
import { DailyReportSectionHeader } from './DailyReportSectionHeader';
import { format } from 'date-fns';

export function BunkerPricesPreview() {
  const { loading, selectedPeriod, fetchBunkerPrices, getGroupedPrices } = useBunkerPricesStore();
  const defaultOpenPort = '0';

  useEffect(() => {
    fetchBunkerPrices();
  }, [fetchBunkerPrices]);

  const groupedPrices = getGroupedPrices();

  const headerAction = (
    <Link to="/bunker-prices" className="inline-flex shrink-0">
      <Button
        variant="ghost"
        size="sm"
        rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}
        className="whitespace-nowrap !px-2 !py-2 sm:!px-3"
        title="View all bunker prices"
      >
        View all
      </Button>
    </Link>
  );

  if (loading) {
    return (
      <div className="space-y-2 sm:space-y-3">
        <DailyReportSectionHeader title="Bunker Prices" action={headerAction} />
        <BunkerPricesPreviewSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      <DailyReportSectionHeader title="Bunker Prices" action={headerAction} />

      {groupedPrices.length > 0 ? (
        <Card noPadding className="overflow-hidden shadow-sm">
          <Accordion
            defaultOpenItem={defaultOpenPort}
            className="!space-y-0 divide-y divide-gray-100 dark:divide-dark-700"
          >
            {groupedPrices.map((group, index) => (
              <Accordion.Item
                key={group.portId}
                id={index.toString()}
                className="rounded-none border-0 shadow-none dark:bg-transparent"
                headerClassName="!px-2 !py-1.5 hover:bg-gray-50 dark:hover:bg-dark-700/80 sm:!px-3 sm:!py-2"
                contentClassName="!px-2 !pb-2 !pt-0 sm:!px-3 sm:!pb-2"
                chevronClassName="h-4 w-4"
                title={
                  <div className="min-w-0 flex-1 text-left">
                    <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                      {group.portName}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {group.prices.length} fuel {group.prices.length === 1 ? 'type' : 'types'}
                    </p>
                  </div>
                }
              >
                <div className="space-y-1">
                  {group.prices.map((price, priceIndex) => {
                    const change = price.change || 0;
                    const isPositive = change > 0;
                    const isNegative = change < 0;

                    return (
                      <div
                        key={`${price.portId}-${price.typeName}-${priceIndex}`}
                        className="flex items-center justify-between gap-2 rounded-md border border-gray-200 px-2 py-1.5 dark:border-dark-600 sm:py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <h4 className="truncate text-xs font-medium text-gray-900 dark:text-white sm:text-sm">
                            {price.typeName}
                          </h4>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 sm:text-[11px]">
                            {format(new Date(price.date), 'MMM d, yyyy')}
                          </p>
                        </div>

                        <div className="shrink-0 text-right tabular-nums">
                          <div className="text-sm font-bold text-gray-900 dark:text-white sm:text-base">
                            ${price.price.toFixed(2)}
                          </div>
                          <div
                            className={`flex items-center justify-end gap-0.5 text-[10px] font-medium sm:text-xs ${
                              isPositive
                                ? 'text-green-600 dark:text-green-400'
                                : isNegative
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {isPositive && <TrendingUp className="h-3 w-3 shrink-0" />}
                            {isNegative && <TrendingDown className="h-3 w-3 shrink-0" />}
                            {change !== 0 && (
                              <span>
                                {isPositive ? '+' : ''}
                                {change.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <Link
                    to={`/bunker-prices?port=${group.portId}&period=${selectedPeriod}`}
                    className="mt-1 flex items-center justify-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    View details
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm dark:border-dark-700 dark:bg-dark-800/50">
          <p className="text-gray-500 dark:text-gray-400">No bunker prices available</p>
        </div>
      )}
    </div>
  );
}
